// Split math. Shared by the server and the browser bundle, so it must stay free of Node and DOM APIs.
// Every amount is an integer number of cents. Nothing here uses floating-point dollars.
import type { Cents, Item, Leftover, Paid, SplitForMath } from './types.ts';
import { friendFeeCents } from './experiments.ts';

export const fmt = (cents: Cents): string => {
  const neg = cents < 0;
  const s = (Math.abs(cents) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '−$' : '$') + s;
};
export const plain = (cents: Cents): string => (cents / 100).toFixed(2);

/**
 * How far our sum is from a printed total, after allowing for rounding. Within a cent counts as a
 * match, since some registers round the total separately. Registers in many countries also round the
 * grand total to a whole unit ("Grand Total 523.00" for 522.60), so a whole-number total may be up to
 * half a unit off, but only on a receipt priced in whole units whose lines match the printed subtotal
 * exactly. That keeps a missed 20-cent fee on a $10.00 + tax = $11.00 receipt from passing as rounding
 * only when the prices themselves have cents, which is the usual case for US receipts.
 */
export function offFromTotal(ours: Cents, printed: Cents, lines: Array<{ cents: Cents }>, subtotal: Cents | null): Cents {
  const off = Math.abs(ours - printed);
  if (off <= 1) return 0;
  const sum = lines.reduce((a, l) => a + l.cents, 0);
  const wholeUnits = lines.every((l) => l.cents % 100 === 0) && sum === subtotal;
  if (wholeUnits && printed % 100 === 0 && off < 50) return 0;
  return off;
}

/** Divide `cents` across `ids` as evenly as possible; leftover cents go to the first ids. */
export function divide(cents: Cents, ids: string[]): Record<string, Cents> {
  const out: Record<string, Cents> = {};
  if (!ids.length) return out;
  const base = Math.floor(cents / ids.length);
  let rem = cents - base * ids.length;
  for (const id of ids) {
    out[id] = base + (rem > 0 ? 1 : 0);
    if (rem > 0) rem--;
  }
  return out;
}

/**
 * Split `amount` in proportion to `weights` so the parts add up exactly. Each part is rounded down,
 * then the cents left over go to the largest remainders; ties go to the earliest key (the payer is
 * listed first, so a friend is never the one rounded up on a tie). Rounding each part on its own
 * instead can make the parts add up to a cent or two more or less than the bill.
 */
export function allocate(amount: Cents, weights: Array<[string, Cents]>, totalWeight: Cents): Record<string, Cents> {
  const out: Record<string, Cents> = {};
  if (totalWeight <= 0) { for (const [id] of weights) out[id] = 0; return out; }
  const sumW = weights.reduce((a, [, w]) => a + w, 0);
  const target = sumW === totalWeight ? amount : Math.round((amount * sumW) / totalWeight);
  const parts = weights.map(([id, w], i) => {
    const exact = (amount * w) / totalWeight;
    return { id, i, floor: Math.floor(exact), frac: exact - Math.floor(exact) };
  });
  let rem = target - parts.reduce((a, p) => a + p.floor, 0);
  for (const p of parts) out[p.id] = p.floor;
  for (const p of [...parts].sort((a, b) => b.frac - a.frac || a.i - b.i)) {
    if (rem <= 0) break;
    out[p.id]++;
    rem--;
  }
  return out;
}

export const subtotalOf = (split: Pick<SplitForMath, 'items'>): Cents => split.items.reduce((a, it) => a + it.cents, 0);

export const claimersOf = (split: Pick<SplitForMath, 'claims'>, itemId: string): string[] =>
  (split.claims[itemId] || []).map((c) => c.pid);

export interface ItemShare {
  itemId: string;
  name: string;
  cents: Cents;
  whole: Cents;
  ways: number;
}

export interface PersonShare {
  id: string;
  name: string;
  isPayer: boolean;
  items: ItemShare[];
  itemsSub: Cents;
  leftoverSub: Cents;
  sub: Cents;
  tax: Cents;
  tip: Cents;
  thanks: Cents;
  /** Their part of the app fee, when the table covers it (fee test group C). Goes to the payer. */
  fee: Cents;
  /** What they'd owe right now. */
  current: Cents;
  paid: Paid | null;
  forgiven: boolean;
  /** Their share of the bill. Always the true share: it follows late claims and leftovers. */
  owed: Cents;
  /** Still to pay: their share minus what they've paid (0 once square, or if let off). */
  due: Cents;
  /** Paid more than their share (it went down after they paid): the payer owes this back. */
  refund: Cents;
}

export interface Totals {
  /** Set when the bill is split evenly: how many ways, one seat's share, and seats nobody has taken. */
  even?: { ways: number; share: Cents; missing: number };
  subtotal: Cents;
  tax: Cents;
  tip: Cents;
  total: Cents;
  people: Record<string, PersonShare>;
  unclaimed: { items: Item[]; sub: Cents; total: Cents };
  payer: {
    breakdown: { sub: Cents; tax: Cents; tip: Cents; thanks: Cents; fee: Cents };
    /** What the payer owes back to people whose share went down after they paid. */
    refunds: Cents;
    comingBack: Cents;
    received: Cents;
    forgiven: Cents;
    outstanding: Cents;
    paidCount: number;
    friendCount: number;
    cost: Cents;
    settled: boolean;
  };
}

/**
 * Work out what everyone owes.
 * Before the split is closed, unclaimed items are reported separately.
 * After it's closed, they're handed out by split.leftover.
 */
export function compute(split: SplitForMath): Totals {
  if (split.even) return computeEven(split);
  const subtotal = subtotalOf(split);
  const tax = split.taxCents || 0;
  const tip = split.tipCents || 0;
  const total = subtotal + tax + tip;
  const payer = split.people.find((p) => p.isPayer);
  const closed = split.status !== 'open';

  type Draft = Pick<PersonShare, 'id' | 'name' | 'isPayer' | 'items' | 'itemsSub' | 'leftoverSub'>;
  const drafts: Record<string, Draft> = {};
  for (const p of split.people) {
    drafts[p.id] = { id: p.id, name: p.name, isPayer: !!p.isPayer, items: [], itemsSub: 0, leftoverSub: 0 };
  }

  const unclaimed: Item[] = [];
  for (const it of split.items) {
    const ids = claimersOf(split, it.id).filter((id) => drafts[id]);
    if (!ids.length) { unclaimed.push(it); continue; }
    const shares = divide(it.cents, ids);
    for (const id of ids) {
      drafts[id].items.push({ itemId: it.id, name: it.name, cents: shares[id], whole: it.cents, ways: ids.length });
      drafts[id].itemsSub += shares[id];
    }
  }
  const unclaimedSub = unclaimed.reduce((a, it) => a + it.cents, 0);

  // Leftovers only move onto people once the payer closes the split.
  let leftoverTo: Record<string, Cents> = {};
  if (closed && unclaimedSub > 0 && payer) {
    const lo: Leftover = split.leftover || { mode: 'even' };
    if (lo.mode === 'assign' && lo.assignTo && drafts[lo.assignTo]) leftoverTo = { [lo.assignTo]: unclaimedSub };
    else if (lo.mode === 'cover') leftoverTo = { [payer.id]: unclaimedSub };
    else {
      // Even: payer first so the odd cents land on the payer.
      const ids = [payer.id, ...split.people.filter((p) => !p.isPayer).map((p) => p.id)];
      leftoverTo = divide(unclaimedSub, ids);
    }
  }
  for (const id in leftoverTo) drafts[id].leftoverSub = leftoverTo[id];

  // Tax and tip are shared in proportion to each person's items. While the split is open, the
  // unclaimed items hold their share too, so everything adds up to the bill exactly.
  const UNCLAIMED = '\u0000unclaimed';
  const ordered = [...split.people].sort((a, b) => Number(!!b.isPayer) - Number(!!a.isPayer));
  const weights: Array<[string, Cents]> = ordered.map((p) => [p.id, drafts[p.id].itemsSub + drafts[p.id].leftoverSub]);
  const unclaimedWeight = Object.keys(leftoverTo).length ? 0 : unclaimedSub;
  if (unclaimedWeight) weights.push([UNCLAIMED, unclaimedWeight]);
  const taxShare = allocate(tax, weights, subtotal);
  const tipShare = allocate(tip, weights, subtotal);
  const thanksCents = split.thanksOn ? (split.thanksCents ?? 100) : 0;
  const feeCents = friendFeeCents(split.fee, split.partySize || 0, split.people.length);

  const people: Record<string, PersonShare> = {};
  for (const p of split.people) {
    const d = drafts[p.id];
    // Group C after lock-in: only the friends there at lock-in share the fee.
    const fee = split.fee?.friendIds && !split.fee.friendIds.includes(p.id) ? 0 : feeCents;
    people[p.id] = share(p, d, { sub: d.itemsSub + d.leftoverSub, tax: taxShare[p.id] ?? 0, tip: tipShare[p.id] ?? 0 }, thanksCents, fee);
  }
  const unclaimedTotal = closed ? 0 : unclaimedSub + (taxShare[UNCLAIMED] ?? 0) + (tipShare[UNCLAIMED] ?? 0);
  return totals(split, { subtotal, tax, tip, total }, people, { items: unclaimed, sub: unclaimedSub, total: unclaimedTotal });
}

type Person = SplitForMath['people'][number];
type Parts = { sub: Cents; tax: Cents; tip: Cents };

/** One person's line, from their part of the food, tax and tip. */
function share(
  p: Person,
  d: Pick<PersonShare, 'id' | 'name' | 'isPayer' | 'items' | 'itemsSub' | 'leftoverSub'>,
  parts: Parts,
  thanksCents: Cents,
  feeCents: Cents,
): PersonShare {
  const r: PersonShare = {
    ...d,
    ...parts,
    thanks: !p.isPayer && parts.sub > 0 ? thanksCents : 0,
    fee: !p.isPayer && parts.sub > 0 ? feeCents : 0,
    current: 0,
    paid: p.paid || null,
    forgiven: !!p.forgiven,
    owed: 0,
    due: 0,
    refund: 0,
  };
  r.current = r.sub + r.tax + r.tip + r.thanks + r.fee;
  // Nobody quietly eats anyone else's share: if a share changes after someone paid, they see the
  // difference and settle it (or the payer pays it back, or chooses to let it go).
  r.owed = r.current;
  const paidCents = r.paid ? r.paid.cents : 0;
  r.due = r.forgiven ? 0 : Math.max(0, r.current - paidCents);
  r.refund = r.paid ? Math.max(0, paidCents - r.current) : 0;
  return r;
}

/**
 * A friend whose share is $0 (they had nothing): nothing to pay, so they count as paid. Worked out
 * from the share itself, never marked by anyone, and it goes away if they pick something later.
 */
export const nothingToPay = (f: Pick<PersonShare, 'current' | 'paid' | 'forgiven'>): boolean => f.current === 0 && !f.paid && !f.forgiven;

/** Square: paid in full (or more), let off, or nothing to pay. */
export const isSquare = (f: Pick<PersonShare, 'current' | 'paid' | 'forgiven' | 'due'>): boolean =>
  f.forgiven || (f.due === 0 && (!!f.paid || nothingToPay(f)));

/** How many ways an even split goes: the table size the payer set, or everyone on it if more joined. */
export const evenWays = (split: Pick<SplitForMath, 'partySize' | 'people'>): number =>
  Math.max(split.partySize || 0, split.people.length, 1);

/**
 * Split evenly: the whole bill (tax and tip included) divided by the number at the table. Each friend
 * pays one seat, rounded down to the cent, so a friend is never the one rounded up; the payer's own
 * seat takes the odd cents. Seats nobody has taken show as unclaimed while the split is open, and
 * stay with the payer once it's closed (or until the payer adds those people).
 */
function computeEven(split: SplitForMath): Totals {
  const subtotal = subtotalOf(split);
  const tax = split.taxCents || 0;
  const tip = split.tipCents || 0;
  const total = subtotal + tax + tip;
  const closed = split.status !== 'open';
  const ways = evenWays(split);
  const seat = Math.floor(total / ways);
  const a = allocate(seat, [['sub', subtotal], ['tax', tax], ['tip', tip]], total);
  const seatParts: Parts = { sub: a.sub ?? 0, tax: a.tax ?? 0, tip: a.tip ?? 0 };
  const missing = ways - split.people.length;
  const friendCount = split.people.filter((p) => !p.isPayer).length;
  // The payer takes whatever the friends' seats (and, while open, the empty seats) don't.
  const others = friendCount + (closed ? 0 : missing);
  const payerParts: Parts = {
    sub: subtotal - others * seatParts.sub,
    tax: tax - others * seatParts.tax,
    tip: tip - others * seatParts.tip,
  };
  const thanksCents = split.thanksOn ? (split.thanksCents ?? 100) : 0;
  const feeCents = friendFeeCents(split.fee, ways, split.people.length);
  const people: Record<string, PersonShare> = {};
  for (const p of split.people) {
    const parts = p.isPayer ? payerParts : seatParts;
    const fee = split.fee?.friendIds && !split.fee.friendIds.includes(p.id) ? 0 : feeCents;
    people[p.id] = share(p, { id: p.id, name: p.name, isPayer: !!p.isPayer, items: [], itemsSub: parts.sub, leftoverSub: 0 }, parts, thanksCents, fee);
  }
  const t = totals(split, { subtotal, tax, tip, total }, people, { items: [], sub: 0, total: closed ? 0 : missing * seat });
  t.even = { ways, share: seat, missing };
  return t;
}

function totals(
  split: SplitForMath,
  bill: { subtotal: Cents; tax: Cents; tip: Cents; total: Cents },
  people: Record<string, PersonShare>,
  unclaimed: Totals['unclaimed'],
): Totals {
  const { total } = bill;
  const closed = split.status !== 'open';
  const friends = split.people.filter((p) => !p.isPayer).map((p) => people[p.id]);
  const sum = (key: 'sub' | 'tax' | 'tip' | 'thanks' | 'fee' | 'current' | 'owed' | 'due' | 'refund') => friends.reduce((a, f) => a + f[key], 0);
  const comingBack = sum('owed');
  const received = friends.reduce((a, f) => a + (f.paid ? f.paid.cents : 0), 0);
  // What the payer chose to let go: whatever was still unpaid for those people.
  const forgiven = friends.filter((f) => f.forgiven).reduce((a, f) => a + Math.max(0, f.current - (f.paid ? f.paid.cents : 0)), 0);

  return {
    ...bill,
    people,
    unclaimed,
    payer: {
      breakdown: { sub: sum('sub'), tax: sum('tax'), tip: sum('tip'), thanks: sum('thanks'), fee: sum('fee') },
      refunds: sum('refund'),
      comingBack,
      received,
      forgiven,
      outstanding: sum('due'),
      /** Square: paid in full (or more), let off, or nothing to pay. */
      paidCount: friends.filter(isSquare).length,
      friendCount: friends.length,
      cost: total - comingBack + forgiven,
      settled: closed && friends.length > 0 && friends.every(isSquare),
    },
  };
}
